import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SubmitPredictionInput } from '@pandacalcio/validation';
import { api } from '../../lib/api';

// ------------------------------------------------------------
// Tipi
// ------------------------------------------------------------

export interface MatchdayListItem {
  id: string;
  number: number;
  state: string;
  opensAt: string | null;
  deadline: string | null;
  publishedAt: string | null;
  isSuper: boolean;
  matchCount: number;
  predictionCount: number;
}

export interface MatchInfo {
  id: string;
  kickoffAt: string;
  status: string;
  homeTeam: { name: string; shortName: string; crestUrl?: string | null };
  awayTeam: { name: string; shortName: string; crestUrl?: string | null };
  homeScore: number | null;
  awayScore: number | null;
  /** Fasce snapshotate per la giornata (1, 2, 3) — per mostrare i valori dei segni. */
  homeTier?: number;
  awayTier?: number;
}

export interface MatchdayDetail {
  id: string;
  number: number;
  state: string;
  deadline: string | null;
  isSuper: boolean;
  matches: MatchInfo[];
}

export interface MyPrediction {
  id: string;
  submittedAt: string;
  isRedColumn: boolean;
  locked: boolean;
  items: Array<{ matchId: string; pick: string }>;
  exact: { matchId: string; home: number; away: number } | null;
  overMatchId: string | null;
}

export interface ScoreRow {
  memberId: string;
  displayName: string;
  isBot: boolean;
  base: number;
  exactResult: number;
  over: number;
  originality: number;
  bonus: number;
  penalty: number;
  total: number;
  manuallyAdjusted: boolean;
  breakdown?: Array<{ type: string; label: string; points: number; matchId?: string }>;
}

export interface StandingRow {
  position: number;
  memberId: string;
  displayName: string;
  isBot: boolean;
  totalPoints: number;
  positionDelta: number;
  playedCount: number;
}

export interface SeasonMatchdayOption {
  id: string;
  number: number;
  matchCount: number;
  alreadyInLeague: boolean;
  firstKickoffAt: string | null;
}

export interface MatchdayStatusRow {
  memberId: string;
  displayName: string;
  isBot: boolean;
  hasSubmitted: boolean;
  submittedAt: string | null;
}

// ------------------------------------------------------------
// Query
// ------------------------------------------------------------

export function useMatchdays(leagueId: string) {
  return useQuery({
    queryKey: ['leagues', leagueId, 'matchdays'],
    queryFn: () =>
      api<{ matchdays: MatchdayListItem[] }>(`/leagues/${leagueId}/matchdays`).then((r) => r.matchdays),
    enabled: !!leagueId,
  });
}

export interface TierCell {
  home: number;
  draw: number;
  away: number;
}

export interface MatchdayRules {
  fixedCount: number;
  allowDouble: boolean;
  doubleDivisor: number;
  /** Punti dei segni per combinazione di fasce ("1-3" = prima contro terza). */
  tierMatrix: Record<string, TierCell>;
  exactEnabled: boolean;
  exactBonus: number;
  overEnabled: boolean;
  overRequired: boolean;
  overLevels: Array<{ goals: number; points: number }>;
  overPenalty: number;
}

export function useMatchday(leagueId: string, matchdayId: string) {
  return useQuery({
    queryKey: ['leagues', leagueId, 'matchdays', matchdayId],
    queryFn: () =>
      api<{ rules: MatchdayRules; matchday: MatchdayDetail; myPrediction: MyPrediction | null }>(
        `/leagues/${leagueId}/matchdays/${matchdayId}`,
      ),
    enabled: !!leagueId && !!matchdayId,
    // I risultati delle partite in corso arrivano dal cron: teniamoli freschi.
    refetchInterval: 60_000,
  });
}

export interface RevealedPrediction {
  memberId: string;
  displayName: string;
  isBot: boolean;
  isRedColumn: boolean;
  items: Array<{ matchId: string; pick: string }>;
  exact: { matchId: string; home: number; away: number } | null;
  overMatchId: string | null;
}

/** Schedine di tutti (visibili solo dopo la chiusura dei pronostici). */
export function useMatchdayPredictions(leagueId: string, matchdayId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['leagues', leagueId, 'matchdays', matchdayId, 'predictions'],
    queryFn: () =>
      api<{ predictions: RevealedPrediction[] }>(
        `/leagues/${leagueId}/matchdays/${matchdayId}/predictions`,
      ).then((r) => r.predictions),
    enabled: !!leagueId && !!matchdayId && enabled,
  });
}

export interface LiveStandingRow {
  position: number;
  memberId: string;
  displayName: string;
  isBot: boolean;
  provisionalPoints: number;
  cumulativePoints: number;
}

/** Classifica live provvisoria durante le partite. */
export function useLiveStandings(leagueId: string, matchdayId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['leagues', leagueId, 'matchdays', matchdayId, 'live'],
    queryFn: () =>
      api<{ rows: LiveStandingRow[]; matchesWithResult: number; totalMatches: number }>(
        `/leagues/${leagueId}/matchdays/${matchdayId}/live`,
      ),
    enabled: !!leagueId && !!matchdayId && enabled,
    refetchInterval: 60_000,
  });
}

export function useMatchdayScores(leagueId: string, matchdayId: string, enabled = true) {
  return useQuery({
    queryKey: ['leagues', leagueId, 'matchdays', matchdayId, 'scores'],
    queryFn: () =>
      api<{ state: string; scores: ScoreRow[] }>(`/leagues/${leagueId}/matchdays/${matchdayId}/scores`),
    enabled: !!leagueId && !!matchdayId && enabled,
    retry: false,
  });
}

export function useStandings(leagueId: string) {
  return useQuery({
    queryKey: ['leagues', leagueId, 'standings'],
    queryFn: () =>
      api<{ matchdayNumber: number | null; rows: StandingRow[] }>(`/leagues/${leagueId}/standings`),
    enabled: !!leagueId,
  });
}

export function useSeasonMatchdays(leagueId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['leagues', leagueId, 'season-matchdays'],
    queryFn: () =>
      api<{ seasonMatchdays: SeasonMatchdayOption[] }>(`/leagues/${leagueId}/season-matchdays`).then(
        (r) => r.seasonMatchdays,
      ),
    enabled: !!leagueId && enabled,
  });
}

export function useMatchdayStatus(leagueId: string, matchdayId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['leagues', leagueId, 'matchdays', matchdayId, 'status'],
    queryFn: () =>
      api<{ total: number; submitted: number; members: MatchdayStatusRow[] }>(
        `/leagues/${leagueId}/matchdays/${matchdayId}/status`,
      ),
    enabled: !!leagueId && !!matchdayId && enabled,
  });
}

// ------------------------------------------------------------
// Mutation
// ------------------------------------------------------------

export function useSubmitPrediction(leagueId: string, matchdayId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SubmitPredictionInput) =>
      api<{ message: string }>(`/leagues/${leagueId}/matchdays/${matchdayId}/prediction`, {
        method: 'PUT',
        body: input,
      }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['leagues', leagueId, 'matchdays', matchdayId] }),
  });
}

export function useCreateMatchday(leagueId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { seasonMatchdayId: string; deadline?: string }) =>
      api<{ matchday: { id: string } }>(`/leagues/${leagueId}/matchdays`, {
        method: 'POST',
        body: input,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['leagues', leagueId, 'matchdays'] }),
  });
}

export function useTransitionMatchday(leagueId: string, matchdayId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (to: string) =>
      api<{ matchday: { state: string } }>(`/leagues/${leagueId}/matchdays/${matchdayId}/transition`, {
        method: 'POST',
        body: { to },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leagues', leagueId, 'matchdays'] });
      queryClient.invalidateQueries({ queryKey: ['leagues', leagueId, 'standings'] });
    },
  });
}

export function useSetResult(leagueId: string, matchdayId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { matchId: string; homeScore: number; awayScore: number }) =>
      api(`/leagues/${leagueId}/matchdays/${matchdayId}/results`, { method: 'PUT', body: input }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['leagues', leagueId, 'matchdays', matchdayId] }),
  });
}

export function useUpdateMatchday(leagueId: string, matchdayId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { deadline?: string }) =>
      api(`/leagues/${leagueId}/matchdays/${matchdayId}`, { method: 'PATCH', body: input }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['leagues', leagueId, 'matchdays'] }),
  });
}
